import { useEffect } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesConnectionPromptMessages } from "@/i18n/messages/frames-connection-prompt";
import { cn } from "@/lib/cn";
import { connectionPromptSlugs } from "@/lib/composio";
import { useIntegrationStore } from "@/stores/integration-store";
import { useFrameSurface } from "../shared/use-frame-surface";
import { ConnectionSetup } from "./connection-setup";

/** A Connect card: one row per app the agent needs, each connected on its own. */
export function ConnectionPromptFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesConnectionPromptMessages);
  const frameSurface = useFrameSurface(id);
  // The agent may supply slugs in any case; the store keys connections by
  // the backend's canonical lowercased slug.
  const toolkitSlugs = connectionPromptSlugs(data.content);
  const reason = (data.content?.reason as string | undefined) ?? "";
  const count = toolkitSlugs.length;
  const allConnected = useIntegrationStore(
    (state) =>
      count > 0 &&
      toolkitSlugs.every(
        (slug) => state.connections.get(slug)?.status === "active",
      ),
  );
  const refreshConnections = useIntegrationStore(
    (state) => state.actions.refreshConnections,
  );

  // Refresh persisted frame hints against the account's actual connection state.
  useEffect(() => {
    void refreshConnections();
  }, [refreshConnections]);

  return (
    <div
      className={cn(
        frameSurface.className,
        "flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="connection-prompt-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "scrollbar-none nodrag flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 [--denk-frame-content-padding-block-end:1rem] [--denk-frame-content-padding-block-start:1rem]",
          selected && "nowheel",
        )}
        data-frame-content="true"
        data-testid="connection-prompt-frame-body"
      >
        <div className="flex items-center gap-2">
          {allConnected && (
            <Icons.CheckCircle
              className="h-5 w-5 shrink-0 text-accent"
              data-testid="connection-prompt-all-connected-icon"
            />
          )}
          <p
            className="text-canvas-base font-medium text-primary"
            data-testid="connection-prompt-title"
          >
            {allConnected
              ? t("allConnected", { count })
              : t("connectApps", { count: Math.max(count, 1) })}
          </p>
        </div>
        {reason && !allConnected && (
          <p className="text-canvas-sm text-secondary">{reason}</p>
        )}
        <ul
          className="flex flex-col gap-3"
          data-testid="connection-prompt-rows"
        >
          {toolkitSlugs.map((slug) => (
            <li key={slug}>
              <ConnectionSetup toolkitSlug={slug} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
